import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Análisis por tipo de contaminación",
    menuTitle: "Tipo de contaminación",
    text: `La última medición (2024) muestra los mejores resultados históricos para todas las categorías estudiadas. En la mayoría de los casos, el año 2017 representó el 
      punto crítico (el de mayores índices), registrándose una tendencia a la baja constante desde entonces.
El cambio más significativo se observa en la presencia de plagas, que se redujo a la mitad entre 2015 y 2024. Por el contrario, la contaminación acústica y la 
      acumulación de basura han mostrado más bien estabilidad estadística, lo que sugiere que son problemáticas más complejas de mitigar. Finalmente, destaca 
      la contaminación del agua de red pública como el indicador con menor incidencia, alcanzando un 0,7% en la medición más reciente.`,
    link: "https://public.flourish.studio/visualisation/28188162/embed",
  },
  {
    id: "graph2",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `En Chile, cerca de 1 de cada 10 niñas y niños vive en hogares que declaran tener al menos dos problemas de contaminación en su entorno cercano. Entre 
          2017 y 2022 este problema disminuyó significativamente, sin embargo, desde ese entonces, la tendencia se estabilizó en torno al 9%.`,
    link: "https://public.flourish.studio/visualisation/25083187/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `Entre 2015 y 2024, disminuyó en todos los tramos etarios la proporción de niñas y niños que viven en hogares con exposición a al menos dos tipos de 
          contaminación. En particular, la última medición revela que esta problemática afecta de manera transversal, sin presentar diferencias significativas 
          según la etapa del desarrollo.`,
    link: "https://public.flourish.studio/visualisation/25083640/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `Si bien la exposición a la contaminación disminuyó entre 2015 y 2024 tanto para niñas y niños chilenos como extranjeros, la última medición muestra 
          que estos últimos presentan una prevalencia significativamente menor. En 2024, se consolida una diferencia de dos puntos porcentuales entre ambos grupos.`,
    link: "https://public.flourish.studio/visualisation/25083768/embed",
  },
  {
    id: "graph5",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `No se observaron diferencias estadísticamente significativas entre niñas y niños que se autoidentifican con pueblos originarios y quienes no para ningún 
          año de medición.`,
    link: "https://public.flourish.studio/visualisation/25083837/embed",
  },
  {
    id: "graph6",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `Un 10% de las niñas y los niños que viven en zonas urbanas está expuesto a contaminación en sus entornos, mientras que este porcentaje disminuye al 6% 
          en zonas rurales. Entre quienes residen en zonas urbanas hubo una disminución significativa de la exposición a al menos dos problemas de contaminación 
          entre 2017 y 2022, que se estabilizó al 2024; sin embargo, en las zonas rurales el indicador ha mostrado estabilidad durante casi una década.`,
    link: "https://public.flourish.studio/visualisation/25084298/embed",
  },
  {
    id: "graph7",
    title: "Análisis por quintil de ingreso",
    menuTitle: "\u2022 Quintil de ingreso",
    text: `La exposición a la contaminación en Chile mantiene una estrecha relación con el nivel socioeconómico de los hogares. La última medición revela que la 
          brecha se concentra en los extremos: las niñas y niños de los quintiles de menores ingresos (I y II, con 11% y 10% respectivamente) presentan una exposición 
          significativamente superior a la del quintil de mayores ingresos (6%). Sin embargo, al comparar a los grupos más vulnerables con los sectores medios (III y 
          IV, con 9% y 8% respectivamente), aunque sus promedios son más altos, no se observan diferencias estadísticamente significativas. Con este resultado, el 
          quintil de mayores ingresos consolida la proporción más baja de la serie histórica, evidenciando una brecha de desigualdad estructural. Se ha documentado 
          que niñas y niños que viven en hogares con carencias económicas son más propensos a vivir en barrios contaminados.[5]`,
    link: "https://public.flourish.studio/visualisation/25084184/embed",
  },
  {
    id: "graph8",
    title: "Análisis por macrozona",
    menuTitle: "\u2022 Macrozona",
    text: `Hay diferencias en la exposición a la contaminación según la macrozona en que residen niñas y niños. Por un lado, destaca la distribución desigual 
          de la Región Metropolitana (RM) frente al resto del país. En 2024, la RM presenta una prevalencia de contaminación del 13%, cifra que supera significativamente 
          a las macrozonas Norte (9%), Centro (7%) y Sur (5%), llegando incluso a duplicar la exposición observada en el sur de Chile.

Por otro lado, a nivel histórico, se observa una mejora en todas las macrozonas entre 2015 y 2024, sin embargo, al observar el periodo reciente (2022-2024), nos encontramos 
      ante un estancamiento generalizado: en ninguna macrozona la disminución fue estadísticamente significativa en los últimos dos años.`,
    link: "https://public.flourish.studio/visualisation/25084248/embed",
  },
  {
    id: "graph9",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `Hacia el 2024, la distribución geográfica de la contaminación en la niñez muestra una marcada brecha entre el sur y las regiones del centro del país 
          (excepto por Antofagasta). Por un lado, las regiones del sur se consolidan como las zonas con menores niveles de exposición, donde destacan las regiones 
          de Los Ríos (2%), Magallanes (3%) y La Araucanía (3%). En el extremo opuesto, la Región Metropolitana registra la mayor prevalencia a nivel nacional con 
          un 13%, seguida por Antofagasta (12%) y O'Higgins (10%). Resulta destacable la evolución de la región de Atacama, que tras iniciar la serie en 2015 con 
          un 24% —el índice más alto registrado en el estudio— logró reducir su exposición al 8% en 2024.`,
    link: "https://public.flourish.studio/visualisation/28157049/embed",
  },
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const indentedGraphs = ["graph1", "graph2"];

  const handleMenuItemClick = (graphId) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  const CustomLink = ({ href, children }) => {
  const target = href && href.startsWith('http') ? '_top' : '_self';
    return (
      <a href={href} target={target}>
        {children}
      </a>
    );
  };

  return (
    <Layout style={{ minHeight: "100vh", display: "flex" }}>
          <Sider
            width={250}
            collapsible
            collapsed={collapsed}
            onCollapse={toggleCollapsed}
            style={{
              background: "#fff",
              height: "100vh",
            }}
            breakpoint="lg"
            collapsedWidth={0}
          >
            <div style={{ overflowY: "auto", height: "100%" }}>
              <Menu
                theme="dark"
                mode="vertical"
                selectedKeys={[selectedMenuItem]} 
                style={{ background: "#fff", height: "100%" }}
              >
                {graphs.map((graph) => (
                  <Menu.Item
                    key={graph.id}
                    onClick={() => handleMenuItemClick(graph.id)}
                    style={{
                      color: "black",
                      paddingLeft: indentedGraphs.includes(graph.id) ? "10px" : "30px",
                    }}
                  >
                    {graph.menuTitle}
                  </Menu.Item>
                ))}
              </Menu>
            </div>
          </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}> 
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{ a: CustomLink }}
                >
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;


